import React from "react";
import { useTranslation } from "react-i18next";
import { TagInput } from "../../../components/ui";

export const PLAN_OPTIONS = [
  { value: "any", label: "Everyone" },
  { value: "premium", label: "Premium subscribers" },
  { value: "free", label: "Free users" },
  { value: "former", label: "Former subscribers" },
];
export const ACTIVITY_OPTIONS = [
  { value: "any", label: "Any activity" },
  { value: "active", label: "Seen recently" },
  { value: "inactive", label: "Not seen recently" },
  { value: "never", label: "Never came" },
];
export const LANGUAGE_OPTIONS = [
  { value: "", label: "All languages" },
  { value: "fr", label: "French" },
  { value: "en", label: "English" },
];
const DAY_OPTIONS = [7, 14, 30, 60, 90, 180, 365];

export const EMPTY_CRITERIA = { plan: "any", activity: "any", days: 30, donors: false, quiz: false, language: "", countries: [] };

/** The criteria of a segment, as sent to the API (services/segments.js). */
export const criteriaOf = (value = {}) => ({
  plan: value.plan || "any",
  activity: value.activity || "any",
  days: Number(value.days) || 30,
  donors: Boolean(value.donors),
  quiz: Boolean(value.quiz),
  language: value.language || "",
  countries: value.countries || [],
});

/**
 * Segment of users shared by the push notifications and the emails: plan,
 * activity over N days, donors, quiz players, language, countries.
 * `value`: criteria, `onChange(partial)`.
 */
const SegmentCriteria = ({ value, onChange, idPrefix = "segment" }) => {
  const { t } = useTranslation();
  const chips = (name, options) => (
    <div className="ui-chips">
      {options.map((o) => (
        <button key={o.value} type="button" className="ui-chip" aria-pressed={value[name] === o.value} onClick={() => onChange({ [name]: o.value })}>
          {t(o.label)}
        </button>
      ))}
    </div>
  );
  return (
    <div className="notif-segment">
      <div>
        <span className="ui-label">{t("Plan")}</span>
        {chips("plan", PLAN_OPTIONS)}
      </div>
      <div>
        <span className="ui-label">{t("Activity")}</span>
        {chips("activity", ACTIVITY_OPTIONS)}
        {value.activity !== "any" && (
          <div className="segment-days">
            <label htmlFor={`${idPrefix}-days`}>
              {value.activity === "active" ? t("Seen in the last") : value.activity === "inactive" ? t("Not seen for") : t("Registered for more than")}
            </label>
            <select id={`${idPrefix}-days`} className="ui-input" value={value.days} onChange={(e) => onChange({ days: Number(e.target.value) })}>
              {DAY_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {t("{{count}} days", { count: days })}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="segment-checks">
        <label className="ledger-check">
          <input type="checkbox" checked={Boolean(value.donors)} onChange={(e) => onChange({ donors: e.target.checked })} />
          {t("Donors only (at least one donation)")}
        </label>
        <label className="ledger-check">
          <input type="checkbox" checked={Boolean(value.quiz)} onChange={(e) => onChange({ quiz: e.target.checked })} />
          {t("Quiz players only")}
        </label>
      </div>
      <div>
        <span className="ui-label">{t("Language")}</span>
        {chips("language", LANGUAGE_OPTIONS)}
      </div>
      <div className="ui-field">
        <label htmlFor={`${idPrefix}-countries`}>{t("Countries")}</label>
        <TagInput
          id={`${idPrefix}-countries`}
          value={value.countries || []}
          onChange={(countries) => onChange({ countries })}
          placeholder={t("e.g. Côte d'Ivoire, then Enter (empty: all countries)")}
        />
      </div>
    </div>
  );
};

/** Short description of a segment (history). */
export function describeSegment(segment = {}, t) {
  const parts = [];
  if (segment.plan === "premium") parts.push(t("Premium subscribers"));
  else if (segment.plan === "free") parts.push(t("Free users"));
  else if (segment.plan === "former") parts.push(t("Former subscribers"));
  else parts.push(t("Logged-in users"));
  if (segment.activity === "active") parts.push(t("active for {{count}} days", { count: segment.days || 30 }));
  if (segment.activity === "inactive") parts.push(t("inactive for {{count}} days", { count: segment.days || 30 }));
  if (segment.activity === "never") parts.push(t("never seen"));
  if (segment.donors) parts.push(t("donors"));
  if (segment.quiz) parts.push(t("quiz players"));
  if (segment.language) parts.push(segment.language.toUpperCase());
  if (segment.countries?.length) parts.push(segment.countries.join(", "));
  return parts.join(" · ");
}

export default SegmentCriteria;
